import { AnimatePresence } from 'framer-motion'

import { ReceiverCompanyCard, ReceiverCompanyCardSkeleton } from '@apps-common/apps/portal/pages/receivers/components/receiver-tabs'
import { NoResultsCard } from '@apps-common/components/common'
import { RECEIVERS_QUEUE_SIZE_OPTION_10, RECEIVERS_QUEUE_SIZE_OPTION_5 } from '@apps-common/config/receivers'
import { sortAlphabetically } from '@apps-common/helpers/array-sort'
import { useReceiversFilterStore } from '@apps-common/stores'
import { ICloudReceiverModel, IGatewayCompany, IGatewayFilteredCompany, IGatewayPollModel } from '@apps-common/types/portal/cloud-receiver'

interface IProps {
  loading: boolean
  data: ICloudReceiverModel | undefined
  condition?: (c: IGatewayCompany) => boolean
}

export const ReceiverCompanyView: React.FC<IProps> = ({ loading, data, condition }) => {
  const { serverId, companyId, queueSize } = useReceiversFilterStore()

  const companyIds: IGatewayFilteredCompany[] = (
    data?.gateways.reduce(
      (acc: IGatewayFilteredCompany[], curr: IGatewayPollModel) => [
        ...acc,
        ...curr.companies.map((c) => ({ id: c.companyId, name: c.companyName }))
      ],
      []
    ) || []
  )
    .filter((value, index, self) => index === self.findIndex((c) => c.id === value.id))
    .filter((c) => {
      if (companyId) {
        return c.id === companyId
      }

      return true
    })
    .sort((a, b) => sortAlphabetically(a, b, 'name'))

  const gatewayCompanies: IGatewayCompany[] = (
    data?.gateways?.reduce(
      (acc: IGatewayCompany[], curr: IGatewayPollModel) => [
        ...acc,
        ...curr.companies.map((c) => ({
          id: c.companyId,
          timestamp: curr.timeStamp,
          frequency: curr.frequency,
          gateway: curr.serverId,
          company: c
        }))
      ],
      []
    ) || []
  )
    .filter((c) => {
      if (condition) {
        return condition(c)
      }

      return true
    })
    .filter((c) => {
      let result = true

      if (serverId) {
        result = c.gateway === serverId
      }

      if (result && queueSize) {
        let size = 0

        if (queueSize === RECEIVERS_QUEUE_SIZE_OPTION_5.value) {
          size = 4
        }

        if (queueSize === RECEIVERS_QUEUE_SIZE_OPTION_10.value) {
          size = 9
        }

        result = c.company.queueSize > size
      }

      return result
    })

  return (
    <div className='flex flex-wrap gap-[15px]'>
      <AnimatePresence>
        {loading &&
          Array.from(Array(4).keys()).map((i) => (
            <div key={`receiver-down-list-skeleton-${i}`}>
              <ReceiverCompanyCardSkeleton index={0} />
            </div>
          ))}
        {!loading && (
          <>
            {(companyIds?.length === 0 || gatewayCompanies?.length === 0) && (
              <NoResultsCard classes='min-w-[min(100%,300px)]'>No receiver results for current filter</NoResultsCard>
            )}
            {data &&
              companyIds.map((cid, i) => {
                const companies = gatewayCompanies.filter((c) => c.id === cid.id)

                return (
                  companies.length > 0 && (
                    <ReceiverCompanyCard
                      key={`receiver-down-list-card-${cid.id}-${i}`}
                      index={i}
                      id={cid.id}
                      name={cid.name}
                      companies={companies}
                    />
                  )
                )
              })}
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
